import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Share2,
  LayoutDashboard,
  Users,
  UserCheck,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Plus,
  Edit3,
  Copy,
  Check,
  AlertCircle,
  Link2,
} from 'lucide-react';
import { useAffiliatePartners } from '../hooks/useAffiliatePartners';
import { affiliatePartnersService } from '../services/affiliatePartners';
import type {
  AffiliatePartnerItem,
  AffiliatePartnerDetail,
  CreateAffiliatePartnerRequest,
  UpdateAffiliatePartnerRequest,
  UsernameAvailability,
  ReferralStatusValue,
} from '../types/affiliatePartners';
import { cn, formatDateTime, formatNumber, showToast } from '../utils/helpers';
import { Badge } from '../components/ui';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;

/** Debounce for the live username check — long enough not to fire per keypress. */
const USERNAME_CHECK_DELAY_MS = 400;

/**
 * How many partners the Overview aggregates over. The list endpoint caps at
 * 100, and there is no dedicated stats endpoint yet, so the totals below are
 * computed client-side from one page. Fine while the programme is small; the
 * tab says so explicitly once there are more partners than this.
 */
const OVERVIEW_SAMPLE_SIZE = 100;

type TabKey = 'overview' | 'partners';

const REFERRAL_STATUS_VARIANT: Record<
  ReferralStatusValue,
  'default' | 'success' | 'warning' | 'error' | 'info' | 'muted'
> = {
  PENDING: 'warning',
  QUALIFIED: 'info',
  CREDITS_ISSUED: 'success',
  INVALID: 'error',
};

const REFERRAL_STATUS_LABEL: Record<ReferralStatusValue, string> = {
  PENDING: 'Pending',
  QUALIFIED: 'Qualified',
  CREDITS_ISSUED: 'Rewarded',
  INVALID: 'Invalid',
};

// ============================================================================
// Shared bits
// ============================================================================

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}> = ({ page, totalPages, total, onPageChange, loading }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
      <p className="text-xs text-muted-foreground">
        Page {page} of {totalPages} ({formatNumber(total)} total)
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || loading}
          className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4 text-foreground" />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) pageNum = i + 1;
          else if (page <= 3) pageNum = i + 1;
          else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
          else pageNum = page - 2 + i;
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className={cn(
                'w-8 h-8 text-xs rounded-lg transition-colors',
                pageNum === page
                  ? 'bg-primary/20 text-primary font-semibold'
                  : 'text-muted-foreground hover:bg-primary/10'
              )}
            >
              {pageNum}
            </button>
          );
        })}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages || loading}
          className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="h-4 w-4 text-foreground" />
        </button>
      </div>
    </div>
  );
};

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-4">
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
      {title}
    </p>
    {children}
  </div>
);

const StatTile: React.FC<{
  label: string;
  value: string;
  icon: React.ElementType;
  hint?: string;
}> = ({ label, value, icon: Icon, hint }) => (
  <div className="bg-card border border-border rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4 text-primary" />
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
        {label}
      </p>
    </div>
    <p className="text-2xl font-semibold text-foreground">{value}</p>
    {hint && <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>}
  </div>
);

interface FormModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  loading?: boolean;
  submitLabel: string;
  submitDisabled?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}

const FormModal: React.FC<FormModalProps> = ({
  open,
  title,
  subtitle,
  loading,
  submitLabel,
  submitDisabled,
  onSubmit,
  onCancel,
  children,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-background border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-4 border-b border-border">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
            )}
          </div>
          <button
            onClick={onCancel}
            className="p-1 rounded-lg hover:bg-primary/10 transition-colors"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            onClick={onCancel}
            className="px-3 py-2 text-sm rounded-lg text-muted-foreground hover:bg-primary/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={loading || submitDisabled}
            className="px-3 py-2 text-sm rounded-lg bg-primary/20 text-primary font-medium hover:bg-primary/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-[10px] text-muted-foreground mb-1">{children}</label>
);

const textInputClass =
  'w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50';

/**
 * Copy-to-clipboard button for a partner link. Falls back to a hidden textarea
 * when the async Clipboard API is unavailable — the dashboard is also opened
 * over plain http on localhost, where `navigator.clipboard` is undefined.
 */
const CopyLinkButton: React.FC<{
  link: string | null;
  label?: string;
  className?: string;
}> = ({ link, label, className }) => {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    if (!link) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const el = document.createElement('textarea');
        el.value = link;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(true);
      showToast('success', 'Affiliate link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showToast('error', 'Could not copy — select and copy the link manually');
    }
  }, [link]);

  if (!link) return null;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        copy();
      }}
      title={link}
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-colors',
        className
      )}
    >
      {copied ? (
        <Check className="h-3 w-3 text-success" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
      {label ?? 'Copy link'}
    </button>
  );
};

// ============================================================================
// Create modal
// ============================================================================

const CreatePartnerModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (partner: AffiliatePartnerItem) => void;
}> = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '',
    username: '',
    phoneNumber: '',
    email: '',
    address: '',
    notes: '',
  });
  const [creating, setCreating] = useState(false);
  const [availability, setAvailability] = useState<UsernameAvailability | null>(null);
  const [checking, setChecking] = useState(false);

  // Guards against a slow earlier check resolving after a newer one and
  // overwriting it with a stale verdict.
  const checkSeq = useRef(0);

  useEffect(() => {
    if (!open) {
      setForm({
        name: '',
        username: '',
        phoneNumber: '',
        email: '',
        address: '',
        notes: '',
      });
      setAvailability(null);
      setChecking(false);
    }
  }, [open]);

  const username = form.username.trim();

  useEffect(() => {
    if (!username) {
      setAvailability(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    const seq = ++checkSeq.current;
    const timer = setTimeout(async () => {
      try {
        const result = await affiliatePartnersService.checkUsername(username);
        if (seq === checkSeq.current) setAvailability(result);
      } catch {
        // A failed check must not block submitting — the create call runs the
        // same rules server-side and is the real gate.
        if (seq === checkSeq.current) setAvailability(null);
      } finally {
        if (seq === checkSeq.current) setChecking(false);
      }
    }, USERNAME_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [username]);

  const canSubmit =
    form.name.trim().length > 0 &&
    username.length > 0 &&
    !checking &&
    availability?.available !== false;

  const handleCreate = useCallback(async () => {
    if (!canSubmit) return;
    setCreating(true);
    try {
      const body: CreateAffiliatePartnerRequest = {
        name: form.name.trim(),
        username,
      };
      if (form.phoneNumber.trim()) body.phoneNumber = form.phoneNumber.trim();
      if (form.email.trim()) body.email = form.email.trim();
      if (form.address.trim()) body.address = form.address.trim();
      if (form.notes.trim()) body.notes = form.notes.trim();

      const partner = await affiliatePartnersService.createPartner(body);
      showToast('success', `Partner @${partner.username} created`);
      onCreated(partner);
      onClose();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Failed to create partner');
      showToast('error', Array.isArray(message) ? message[0] : message);
    } finally {
      setCreating(false);
    }
  }, [canSubmit, form, username, onCreated, onClose]);

  return (
    <FormModal
      open={open}
      title="Create Affiliate Partner"
      loading={creating}
      submitLabel={creating ? 'Creating...' : 'Create partner'}
      submitDisabled={!canSubmit}
      onSubmit={handleCreate}
      onCancel={onClose}
    >
      <div>
        <FieldLabel>Name *</FieldLabel>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Jane Doe"
          className={textInputClass}
        />
      </div>

      <div>
        <FieldLabel>Username *</FieldLabel>
        <input
          type="text"
          value={form.username}
          onChange={(e) =>
            setForm((f) => ({ ...f, username: e.target.value.trim() }))
          }
          placeholder="e.g. janedoe"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className={textInputClass}
        />
        {/* Rendered only once there is something to say, so an untouched form
            does not carry a blank reserved row under the field. */}
        {username && (
          <div className="mt-1.5">
            {checking && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Checking availability...
              </p>
            )}
            {!checking && availability?.available && (
              <p className="text-[11px] text-success flex items-center gap-1">
                <Check className="h-3 w-3" />
                {/* Deliberately does NOT preview the URL. The link is built
                    server-side (AffiliatePartnersService.buildAffiliateLink,
                    off PROFILE_URL_BASE) and shown from the API once the
                    partner exists — restating its shape here just gives it a
                    second definition to drift out of step with. */}
                Available
              </p>
            )}
            {!checking && availability && !availability.available && (
              <p className="text-[11px] text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {availability.message ?? 'Unavailable'}
              </p>
            )}
          </div>
        )}
      </div>

      <div>
        <FieldLabel>Phone number</FieldLabel>
        <input
          type="tel"
          value={form.phoneNumber}
          onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
          placeholder="+14155550123"
          className={textInputClass}
        />
      </div>

      <div>
        <FieldLabel>Email</FieldLabel>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder="jane@example.com"
          className={textInputClass}
        />
      </div>

      <div>
        <FieldLabel>Address</FieldLabel>
        <textarea
          value={form.address}
          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
          placeholder="Street, city, state, country"
          rows={2}
          className={cn(textInputClass, 'resize-none')}
        />
      </div>

      <div>
        <FieldLabel>Note</FieldLabel>
        <textarea
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          placeholder="Optional notes..."
          rows={2}
          className={cn(textInputClass, 'resize-none')}
        />
      </div>
    </FormModal>
  );
};

// ============================================================================
// Edit modal
// ============================================================================

const EditPartnerModal: React.FC<{
  partner: AffiliatePartnerItem | null;
  onClose: () => void;
  onUpdated: () => void;
}> = ({ partner, onClose, onUpdated }) => {
  const [form, setForm] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    address: '',
    notes: '',
    isActive: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (partner) {
      setForm({
        name: partner.name ?? '',
        phoneNumber: partner.phoneNumber ?? '',
        email: partner.email ?? '',
        address: partner.address ?? '',
        notes: partner.notes ?? '',
        isActive: partner.isActive,
      });
    }
  }, [partner]);

  const handleSave = useCallback(async () => {
    if (!partner) return;
    setSaving(true);
    try {
      const body: UpdateAffiliatePartnerRequest = {
        name: form.name.trim(),
        phoneNumber: form.phoneNumber.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        isActive: form.isActive,
      };
      await affiliatePartnersService.updatePartner(partner.id, body);
      showToast('success', 'Partner updated');
      onUpdated();
      onClose();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Failed to update partner');
      showToast('error', Array.isArray(message) ? message[0] : message);
    } finally {
      setSaving(false);
    }
  }, [partner, form, onUpdated, onClose]);

  return (
    <FormModal
      open={!!partner}
      title="Edit Partner"
      subtitle={partner?.username ? `@${partner.username}` : undefined}
      loading={saving}
      submitLabel={saving ? 'Saving...' : 'Save changes'}
      onSubmit={handleSave}
      onCancel={onClose}
    >
      <div>
        <FieldLabel>Name</FieldLabel>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className={textInputClass}
        />
      </div>
      <div>
        <FieldLabel>Phone number</FieldLabel>
        <input
          type="tel"
          value={form.phoneNumber}
          onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
          placeholder="+14155550123"
          className={textInputClass}
        />
      </div>
      <div>
        <FieldLabel>Email</FieldLabel>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          className={textInputClass}
        />
      </div>
      <div>
        <FieldLabel>Address</FieldLabel>
        <textarea
          value={form.address}
          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
          rows={2}
          className={cn(textInputClass, 'resize-none')}
        />
      </div>
      <div>
        <FieldLabel>Note</FieldLabel>
        <textarea
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          rows={2}
          className={cn(textInputClass, 'resize-none')}
        />
      </div>
      <div className="flex items-start gap-2 pt-1">
        <input
          id="partner-active"
          type="checkbox"
          checked={form.isActive}
          onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          className="mt-0.5"
        />
        <label htmlFor="partner-active" className="text-xs text-foreground">
          Active
          <span className="block text-[10px] text-muted-foreground">
            Turning this off stops the affiliate link resolving. Referrals
            already attributed to them are not affected.
          </span>
        </label>
      </div>
    </FormModal>
  );
};

// ============================================================================
// Detail drawer
// ============================================================================

const PartnerDetailDrawer: React.FC<{
  partnerId: number | null;
  onClose: () => void;
}> = ({ partnerId, onClose }) => {
  const [detail, setDetail] = useState<AffiliatePartnerDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (partnerId == null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    affiliatePartnersService
      .getPartnerDetail(partnerId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) showToast('error', 'Failed to load partner');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  if (partnerId == null) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60">
      <div className="bg-background border-l border-border w-full max-w-lg h-full overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background">
          <h3 className="text-sm font-semibold text-foreground">
            {detail?.name ?? 'Partner'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-primary/10 transition-colors"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && detail && (
          <div className="p-4">
            <DetailSection title="Affiliate link">
              <div className="flex items-center gap-2 flex-wrap">
                <code className="text-xs text-foreground bg-card border border-border rounded-lg px-2 py-1 break-all">
                  {detail.affiliateLink ?? '—'}
                </code>
                <CopyLinkButton link={detail.affiliateLink} />
              </div>
            </DetailSection>

            <DetailSection title="Details">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Username</span>
                  <p className="text-foreground">@{detail.username}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <p>
                    <Badge variant={detail.isActive ? 'success' : 'muted'}>
                      {detail.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Phone</span>
                  <p className="text-foreground">{detail.phoneNumber ?? '—'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Email</span>
                  <p className="text-foreground break-all">{detail.email ?? '—'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Created</span>
                  <p className="text-foreground">{formatDateTime(detail.createdAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">User ID</span>
                  <p className="text-foreground">{detail.id}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Address</span>
                  <p className="text-foreground whitespace-pre-line">
                    {detail.address ?? '—'}
                  </p>
                </div>
              </div>
            </DetailSection>

            {detail.notes && (
              <DetailSection title="Note">
                <p className="text-xs text-foreground whitespace-pre-line bg-card border border-border rounded-lg px-3 py-2">
                  {detail.notes}
                </p>
              </DetailSection>
            )}

            <DetailSection title="Performance">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-card border border-border rounded-lg p-3">
                  <p className="text-lg font-semibold text-foreground">
                    {formatNumber(detail.referralCount)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">Signed up</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-3">
                  <p className="text-lg font-semibold text-foreground">
                    {formatNumber(detail.qualifiedCount)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">Qualified</p>
                </div>
              </div>
            </DetailSection>

            <DetailSection title={`People who joined (${detail.referrals.length})`}>
              {detail.referrals.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Nobody has signed up through this link yet.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {detail.referrals.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between bg-card border border-border rounded-lg px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-xs text-foreground truncate">
                          {r.refereeName ?? `User ${r.refereeId}`}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatDateTime(r.joinedAt)}
                        </p>
                      </div>
                      <Badge variant={REFERRAL_STATUS_VARIANT[r.status]}>
                        {REFERRAL_STATUS_LABEL[r.status]}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </DetailSection>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Tab: Overview
// ============================================================================

const OverviewTab: React.FC<{ onViewPartner: (id: number) => void }> = ({
  onViewPartner,
}) => {
  const params = useMemo(() => ({ limit: OVERVIEW_SAMPLE_SIZE, offset: 0 }), []);
  const { items, total, loading, error } = useAffiliatePartners(params);

  const stats = useMemo(() => {
    const active = items.filter((p) => p.isActive).length;
    const signups = items.reduce((sum, p) => sum + p.referralCount, 0);
    const top = [...items]
      .filter((p) => p.referralCount > 0)
      .sort((a, b) => b.referralCount - a.referralCount)
      .slice(0, 5);
    return { active, signups, top };
  }, [items]);

  const sampled = total > items.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2">
        <AlertCircle className="h-5 w-5 text-destructive" />
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <StatTile label="Partners" value={formatNumber(total)} icon={Share2} />
        <StatTile
          label="Active"
          value={formatNumber(stats.active)}
          icon={UserCheck}
          hint="Link resolving"
        />
        <StatTile
          label="Signed up"
          value={formatNumber(stats.signups)}
          icon={Users}
          hint="Through partner links"
        />
      </div>

      {sampled && (
        <p className="text-[10px] text-muted-foreground mb-4">
          Signup totals cover the {formatNumber(items.length)} most recent
          partners of {formatNumber(total)}.
        </p>
      )}

      <div className="bg-card border border-border rounded-xl p-4">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Top partners by signups
        </p>
        {stats.top.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No partner has landed a signup yet.
          </p>
        ) : (
          <div className="space-y-1.5">
            {stats.top.map((p) => (
              <button
                key={p.id}
                onClick={() => onViewPartner(p.id)}
                className="w-full flex items-center justify-between bg-background border border-border rounded-lg px-3 py-2 hover:bg-primary/5 transition-colors text-left"
              >
                <div className="min-w-0">
                  <p className="text-xs text-foreground truncate">
                    {p.name ?? `@${p.username}`}
                  </p>
                  <p className="text-[10px] text-muted-foreground">@{p.username}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-sm text-foreground">
                    {formatNumber(p.referralCount)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {formatNumber(p.qualifiedCount)} qualified
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Tab: Partners
// ============================================================================

const PartnersTab: React.FC<{
  onViewPartner: (id: number) => void;
  onEditPartner: (partner: AffiliatePartnerItem) => void;
  onCreatePartner: () => void;
  refreshKey: number;
}> = ({ onViewPartner, onEditPartner, onCreatePartner, refreshKey }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      search: search || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [search, page]
  );

  const { items, total, loading, error, totalPages, refetch } =
    useAffiliatePartners(params);

  // Re-fetch when the page-level create/edit flows report a change.
  useEffect(() => {
    if (refreshKey > 0) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, username, email, phone"
            className="w-full pl-9 pr-8 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-primary/10"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
        </div>
        <button
          onClick={onCreatePartner}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-primary/20 text-primary font-medium hover:bg-primary/30 transition-colors flex-shrink-0"
        >
          <Plus className="h-4 w-4" />
          Create partner
        </button>
      </div>

      <div className="bg-card border border-border rounded-xl p-4">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={() => refetch()}
              className="text-xs text-primary hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Link2 className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-foreground">
              {search ? 'No partners match that search' : 'No affiliate partners yet'}
            </p>
            {!search && (
              <p className="text-xs text-muted-foreground max-w-sm text-center">
                Create one to generate their affiliate link — you can share it
                with them straight away.
              </p>
            )}
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Partner
                  </th>
                  <th className="pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Affiliate link
                  </th>
                  <th className="pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider text-right">
                    Signed up
                  </th>
                  <th className="pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider text-right">
                    Qualified
                  </th>
                  <th className="pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => onViewPartner(p.id)}
                    className="border-b border-border/50 last:border-0 hover:bg-primary/5 cursor-pointer transition-colors"
                  >
                    <td className="py-2.5 pr-3">
                      <p className="text-sm text-foreground">{p.name ?? '—'}</p>
                      <p className="text-[10px] text-muted-foreground">
                        @{p.username}
                      </p>
                    </td>
                    <td className="py-2.5 pr-3">
                      <CopyLinkButton link={p.affiliateLink} />
                    </td>
                    <td className="py-2.5 pr-3">
                      <p className="text-xs text-foreground break-all">
                        {p.email ?? '—'}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {p.phoneNumber ?? '—'}
                      </p>
                    </td>
                    <td className="py-2.5 pr-3 text-sm text-foreground text-right">
                      {formatNumber(p.referralCount)}
                    </td>
                    <td className="py-2.5 pr-3 text-sm text-foreground text-right">
                      {formatNumber(p.qualifiedCount)}
                    </td>
                    <td className="py-2.5 pr-3">
                      <Badge variant={p.isActive ? 'success' : 'muted'}>
                        {p.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-2.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditPartner(p);
                        }}
                        title="Edit partner"
                        className="p-1.5 rounded-lg hover:bg-primary/10 transition-colors"
                      >
                        <Edit3 className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          onPageChange={setPage}
          loading={loading}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Page
// ============================================================================

const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'partners', label: 'Partners', icon: Share2 },
];

const AffiliatePartners: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<AffiliatePartnerItem | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  // Bumped whenever a create/edit succeeds so the list tab refetches.
  const [refreshKey, setRefreshKey] = useState(0);

  const bumpRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tab bar */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center gap-1 px-4 py-2 overflow-x-auto scrollbar-thin">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all whitespace-nowrap',
                  isActive
                    ? 'bg-primary/15 text-primary font-medium border border-primary/30'
                    : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {activeTab === 'overview' && (
          <OverviewTab onViewPartner={setDetailId} />
        )}

        {activeTab === 'partners' && (
          <PartnersTab
            onViewPartner={setDetailId}
            onEditPartner={setEditing}
            onCreatePartner={() => setShowCreate(true)}
            refreshKey={refreshKey}
          />
        )}
      </div>

      <CreatePartnerModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setActiveTab('partners');
          bumpRefresh();
        }}
      />

      <EditPartnerModal
        partner={editing}
        onClose={() => setEditing(null)}
        onUpdated={bumpRefresh}
      />

      <PartnerDetailDrawer
        partnerId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
};

export default AffiliatePartners;
